import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import {
  Elements,
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import type { Stripe, StripeCardNumberElementChangeEvent, StripeCardExpiryElementChangeEvent, StripeCardCvcElementChangeEvent } from '@stripe/stripe-js';
import MagicButton from 'components/MagicButton/MagicButton';
import { AssetUrls } from 'config/assets';
import { toast } from 'react-toastify';
import { getStripePromise } from 'config/stripe';
import {
  useCreateSetupIntentMutation,
  useAttachPaymentMethodMutation,
} from 'store/services/subscription/subscriptionApi';
import './AddPaymentMethodModal.css';

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

const ELEMENT_STYLE = {
  base: {
    color: '#fff',
    fontFamily: "'Plus Jakarta Sans', sans-serif",
    fontSize: '14px',
    '::placeholder': { color: '#666' },
    iconColor: '#888',
  },
  invalid: {
    color: '#ef4444',
    iconColor: '#ef4444',
  },
};

const AddPaymentMethodForm: React.FC<{ onClose: () => void; clientSecret: string }> = ({
  onClose,
  clientSecret,
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [attachPaymentMethod] = useAttachPaymentMethodMutation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const [cardNumberComplete, setCardNumberComplete] = useState(false);
  const [cardExpiryComplete, setCardExpiryComplete] = useState(false);
  const [cardCvcComplete, setCardCvcComplete] = useState(false);
  const [zipCode, setZipCode] = useState('');

  const [fieldErrors, setFieldErrors] = useState<Record<string, string | null>>({
    cardNumber: null,
    cardExpiry: null,
    cardCvc: null,
  });

  const zipTrimmed = zipCode.replace(/\s/g, '');
  const zipValid = zipTrimmed.length >= 3 && zipTrimmed.length <= 10 && /^[a-zA-Z0-9]+$/.test(zipTrimmed);
  const allValid = cardNumberComplete && cardExpiryComplete && cardCvcComplete && zipValid;

  const handleCardNumberChange = (event: StripeCardNumberElementChangeEvent) => {
    setCardNumberComplete(event.complete);
    setFieldErrors(prev => ({ ...prev, cardNumber: event.error?.message || null }));
    setSubmitError(null);
  };

  const handleCardExpiryChange = (event: StripeCardExpiryElementChangeEvent) => {
    setCardExpiryComplete(event.complete);
    setFieldErrors(prev => ({ ...prev, cardExpiry: event.error?.message || null }));
    setSubmitError(null);
  };

  const handleCardCvcChange = (event: StripeCardCvcElementChangeEvent) => {
    setCardCvcComplete(event.complete);
    setFieldErrors(prev => ({ ...prev, cardCvc: event.error?.message || null }));
    setSubmitError(null);
  };

  const handleSubmit = async () => {
    if (!stripe || !elements || !allValid) return;

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const result = await stripe.confirmCardSetup(clientSecret, {
        payment_method: {
          card: elements.getElement(CardNumberElement)!,
          billing_details: {
            address: { postal_code: zipCode.trim() },
          },
        },
      });

      if (result.error) {
        setSubmitError(result.error.message || 'Failed to add card.');
        setIsSubmitting(false);
        return;
      }

      if (result.setupIntent?.payment_method) {
        const pmId = typeof result.setupIntent.payment_method === 'string'
          ? result.setupIntent.payment_method
          : result.setupIntent.payment_method.id;

        await attachPaymentMethod({ paymentMethodId: pmId }).unwrap();
        toast.success('Payment method added successfully.');
        onClose();
      }
    } catch {
      toast.error('Failed to add payment method. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <div className="add-pm-modal-body">
        <div className="add-pm-field-group">
          <label className="add-pm-field-label">Card number</label>
          <div className={`add-pm-field-wrapper${fieldErrors.cardNumber ? ' add-pm-field-error-border' : ''}`}>
            <CardNumberElement
              options={{ style: ELEMENT_STYLE, showIcon: true }}
              onChange={handleCardNumberChange}
            />
          </div>
          {fieldErrors.cardNumber && (
            <div className="add-pm-field-error">{fieldErrors.cardNumber}</div>
          )}
        </div>

        <div className="add-pm-field-row">
          <div className="add-pm-field-group">
            <label className="add-pm-field-label">Expiration</label>
            <div className={`add-pm-field-wrapper${fieldErrors.cardExpiry ? ' add-pm-field-error-border' : ''}`}>
              <CardExpiryElement
                options={{ style: ELEMENT_STYLE }}
                onChange={handleCardExpiryChange}
              />
            </div>
            {fieldErrors.cardExpiry && (
              <div className="add-pm-field-error">{fieldErrors.cardExpiry}</div>
            )}
          </div>

          <div className="add-pm-field-group">
            <label className="add-pm-field-label">CVC</label>
            <div className={`add-pm-field-wrapper${fieldErrors.cardCvc ? ' add-pm-field-error-border' : ''}`}>
              <CardCvcElement
                options={{ style: ELEMENT_STYLE }}
                onChange={handleCardCvcChange}
              />
            </div>
            {fieldErrors.cardCvc && (
              <div className="add-pm-field-error">{fieldErrors.cardCvc}</div>
            )}
          </div>
        </div>

        <div className="add-pm-field-group">
          <label className="add-pm-field-label">Billing zip / postal code</label>
          <input
            className="add-pm-zip-input"
            type="text"
            placeholder="12345"
            value={zipCode}
            onChange={(e) => setZipCode(e.target.value)}
            maxLength={10}
            autoComplete="postal-code"
          />
        </div>

        {submitError && <div className="add-pm-error">{submitError}</div>}
      </div>

      <div className="add-pm-modal-footer">
        <MagicButton
          label="Cancel"
          color="dark-grey"
          onPress={onClose}
          isDisabled={isSubmitting}
        />
        <MagicButton
          label={isSubmitting ? 'Adding...' : 'Add Payment Method'}
          color="purple"
          onPress={handleSubmit}
          isLoading={isSubmitting}
          isDisabled={isSubmitting || !stripe || !allValid}
        />
      </div>
    </>
  );
};

const AddPaymentMethodModal: React.FC<Props> = ({ isOpen, onClose }) => {
  const [createSetupIntent] = useCreateSetupIntentMutation();
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [isLoadingIntent, setIsLoadingIntent] = useState(false);
  const [stripeInstance, setStripeInstance] = useState<Promise<Stripe | null> | null>(null);
  const [stripeLoadFailed, setStripeLoadFailed] = useState(false);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (isOpen) {
      setIsLoadingIntent(true);
      setClientSecret(null);
      setStripeLoadFailed(false);

      // Load Stripe lazily when the modal opens
      const stripeP = getStripePromise();
      stripeP.then((s) => {
        if (!s) setStripeLoadFailed(true);
        else setStripeInstance(stripeP);
      });

      createSetupIntent()
        .unwrap()
        .then((res) => setClientSecret(res.clientSecret))
        .catch(() => {
          toast.error('Failed to initialize payment form.');
          onCloseRef.current();
        })
        .finally(() => setIsLoadingIntent(false));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, createSetupIntent]);

  if (!isOpen) return null;

  const modalContent = (
    <div className="add-pm-modal-overlay" onClick={onClose}>
      <div className="add-pm-modal" onClick={(e) => e.stopPropagation()}>
        <div className="add-pm-modal-header">
          <h2 className="add-pm-modal-title">Add Payment Method</h2>
          <button className="add-pm-close-button" onClick={onClose}>
            <img
              src={AssetUrls.icons.dropdowns.close}
              alt="Close"
              style={{ width: 16, height: 16 }}
            />
          </button>
        </div>

        {stripeLoadFailed ? (
          <div className="add-pm-modal-body">
            <div className="add-pm-error">
              Payment processing couldn't be loaded. This may be caused by a content blocker or tracking prevention. Please disable it and reload the page.
            </div>
          </div>
        ) : isLoadingIntent || !clientSecret || !stripeInstance ? (
          <div className="add-pm-modal-body">
            <div className="add-pm-loading">Setting up secure payment form...</div>
          </div>
        ) : (
          <Elements stripe={stripeInstance}>
            <AddPaymentMethodForm onClose={onClose} clientSecret={clientSecret} />
          </Elements>
        )}
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default AddPaymentMethodModal;
